/**
 * SamplesPage — Customer Sample Management Hub
 *
 * Session-scoped: shows ONLY samples for the active session customer.
 * Reports are ALWAYS site-scoped — a report can only be generated for ONE
 * specific site. Samples with no site cannot trigger report generation.
 * No session customer → customer gate (no data shown).
 *
 * HOOKS COMPLIANCE: ALL hooks are called unconditionally before any early
 * returns. Early returns (customer gate, report viewer) come AFTER all hooks.
 *
 * RETROACTIVE SITE ASSIGNMENT: samples entered before sites existed can be
 * linked to a site inline from the samples list. Persisted immediately to
 * both localStorage keys (rb_influent_samples_v2 and rb_customer_samples).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Droplets, Beaker, Building2, FlaskConical, TrendingUp,
  Plus, FileText, ArrowRight, CheckCircle2, AlertTriangle,
  BarChart3, Shield, RefreshCw, PenLine, ChevronRight,
  MapPin, Hash, Lock, Link2, Info, Wand2, ChevronDown, ChevronUp,
  Calendar, Search, Eye, X,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { hasPhpBackend, apiListInfluentSamples, apiListReports, apiSaveReport, apiListCustomerSites } from '@/lib/phpApi';
import { getAllLocalSamples, getLocalSites, type CustomerSite } from '@/lib/hybridStorage';
import { getStandard } from '@/types/standards';
import { buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import ProfessionalReport from '@/components/ProfessionalReport';

/* ─── Types ─────────────────────────────────────────────────────────────── */

interface SampleRow {
  id: number;
  customerId: number | null;
  siteId: string | null;
  sampleName: string;
  reportNumber: string;
  params: Record<string, number>;
  standard: string;
  sampleType: 'influent' | 'effluent';
  createdAt: string;
}

interface ReportRow {
  id: string;
  reportNumber: string;
  reportTitle: string;
  customerName?: string;
  siteName?: string;
  siteAddress?: string;
  sitePermitRef?: string;
  siteJurisdiction?: string;
  samples: { name: string; referenceId?: string; date: string; params: Record<string, number> }[];
  standard: string;
  createdAt: string;
  source: 'server' | 'local';
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function resolveDbCustId(currentCustomer: any): number {
  if (!currentCustomer) return 0;
  if (currentCustomer.serverId) return Number(currentCustomer.serverId);
  const n = Number(currentCustomer.id);
  return currentCustomer.id.length < 13 && n > 0 ? n : 0;
}


function fmtDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

/**
 * Resolve site jurisdiction label from site state/country.
 * This is derived from the site's metadata, NOT from user-entered notes,
 * ensuring regulatory accuracy even if notes contain errors.
 */
function resolveSiteJurisdiction(site: CustomerSite): string {
  const cc = (site.country || '').toUpperCase();
  const state = (site.state || '').toUpperCase();
  if (cc === 'AU' || cc === 'AUS') {
    const auMap: Record<string, string> = {
      NSW: 'NSW Environment Protection Authority (NSW EPA)',
      VIC: 'Environment Protection Authority Victoria (EPA Vic)',
      QLD: 'Department of Environment and Science (Qld DES)',
      SA:  'Environment Protection Authority South Australia (SA EPA)',
      WA:  'Department of Water and Environmental Regulation (WA DWER)',
      TAS: 'Environment Protection Authority Tasmania (EPA Tas)',
      ACT: 'Environment Protection Authority ACT (ACT EPA)',
      NT:  'Environment Protection Authority NT',
    };
    return auMap[state] || 'Australian State Environment Protection Authority';
  }
  if (cc === 'GB' || cc === 'UK') {
    if (site.region?.toLowerCase().includes('scot')) return 'Scottish Environment Protection Agency (SEPA)';
    if (site.region?.toLowerCase().includes('wales') || site.region?.toLowerCase().includes('cymru'))
      return 'Natural Resources Wales (NRW) / Cyfoeth Naturiol Cymru';
    return 'Environment Agency (EA)';
  }
  if (cc === 'US') return 'US Environmental Protection Agency (US EPA)';
  return 'Applicable National Regulatory Authority';
}

/**
 * Assign a site to a sample record in localStorage.
 * Updates both the primary key (rb_influent_samples_v2) and the legacy key
 * (rb_customer_samples) so all lookup paths see the updated siteId.
 */
function persistSampleSiteAssignment(sampleId: number, siteId: string | null) {
  const LS_PRIMARY = 'rb_influent_samples_v2';
  const LS_LEGACY  = 'rb_customer_samples';
  const patch = (key: string) => {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = raw.map((s: any) =>
        (Number(s.id) === sampleId) ? { ...s, siteId } : s
      );
      localStorage.setItem(key, JSON.stringify(updated));
    } catch { /* ignore */ }
  };
  patch(LS_PRIMARY);
  patch(LS_LEGACY);
}

/**
 * Load sites for a customer. Tries strict match first (customerId === id),
 * falls back to lenient numeric comparison to handle DB-id vs local-id mismatch.
 */
function loadSitesForCustomer(customerId: string, serverId?: number): CustomerSite[] {
  const all = getLocalSites(); // no filter
  // Strict match
  const strict = all.filter(s => s.customerId === customerId);
  if (strict.length > 0) return strict;
  // Lenient: match by serverId or numeric equivalence
  if (serverId && serverId > 0) {
    const byServer = all.filter(s =>
      s.serverCustomerId === serverId ||
      Number(s.customerId) === serverId
    );
    if (byServer.length > 0) return byServer;
  }
  // Lenient: numeric match of customerId
  const numId = Number(customerId);
  if (!isNaN(numId) && numId > 0) {
    return all.filter(s => Number(s.customerId) === numId);
  }
  return [];
}

/**
 * Extract a short prefix from a sample name or report number to group
 * samples that likely belong to the same site.
 *
 * Strategy (in priority order):
 *  1. Report number segment: "RB-2026-SYD-001" → "SYD"
 *  2. Name prefix before dash: "SWW-WWTP Dry Season" → "SWW"
 *  3. Name prefix before space (capitalised only): "PFH Standard" → "PFH"
 *  4. First 3 uppercase chars of name as fallback
 */
function extractSamplePrefix(sampleName: string, reportNumber: string): string {
  // 1. Middle segment of report number e.g. RB-2026-SYD-001
  const rn = (reportNumber || '').toUpperCase();
  const rnMatch = rn.match(/[A-Z]{2}-\d{4}-([A-Z]{2,8})-/);
  if (rnMatch) return rnMatch[1];

  // 2. Leading uppercase prefix before dash in sample name
  const nm = (sampleName || '').trim();
  const dashMatch = nm.match(/^([A-Z]{2,8})-/);
  if (dashMatch) return dashMatch[1];

  // 3. First all-caps word before a space
  const spaceMatch = nm.match(/^([A-Z]{2,8})\s/);
  if (spaceMatch) return spaceMatch[1];

  // 4. First 3 chars uppercased
  return nm.substring(0, 3).toUpperCase();
}

/**
 * Heuristic: given a prefix (e.g. "SYD", "PAR", "SWW"), score each site
 * and return the best match. Checks site name words, address words and
 * short abbreviations derived from the site name.
 *
 * Returns null when no site scores above threshold.
 */
function findBestSiteForPrefix(prefix: string, sites: CustomerSite[]): CustomerSite | null {
  if (!prefix || sites.length === 0) return null;
  const p = prefix.toUpperCase();

  const score = (site: CustomerSite): number => {
    let s = 0;
    const text = `${site.name} ${site.address || ''} ${site.state || ''}`.toUpperCase();
    const words = text.split(/[\s,.\-/()&]+/).filter(Boolean);

    // Direct word start match — strong signal
    words.forEach(w => {
      if (w.startsWith(p))          s += 10;
      else if (p.startsWith(w.substring(0, Math.min(3, w.length)))) s += 4;
    });

    // Acronym derived from site name initial letters
    const acronym = site.name
      .split(/\s+/)
      .filter(w => /^[A-Z]/i.test(w))
      .map(w => w[0].toUpperCase())
      .join('');
    if (acronym === p)              s += 15;
    if (acronym.startsWith(p))     s += 8;
    if (p.startsWith(acronym.substring(0, 2))) s += 5;

    return s;
  };

  const scored = sites.map(site => ({ site, score: score(site) }))
    .sort((a, b) => b.score - a.score);

  // Only suggest if there's a meaningful match (score ≥ 4)
  return scored[0]?.score >= 4 ? scored[0].site : null;
}

/** Bulk-persist multiple sample→site assignments in one localStorage pass */
function persistBulkAssignments(assignments: Array<{ sampleId: number; siteId: string | null }>) {
  const map = new Map(assignments.map(a => [a.sampleId, a.siteId]));
  const patch = (key: string) => {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = raw.map((s: any) => {
        const siteId = map.get(Number(s.id));
        return siteId !== undefined ? { ...s, siteId } : s;
      });
      localStorage.setItem(key, JSON.stringify(updated));
    } catch { /* ignore */ }
  };
  patch('rb_influent_samples_v2');
  patch('rb_customer_samples');
}

/* ─── Param display helpers ──────────────────────────────────────────────── */

const PARAM_LABELS: Record<string, string> = {
  ph: 'pH', bod: 'BOD', cod: 'COD', tss: 'TSS', nh3: 'NH₃-N',
  og: 'O&G', oandg: 'O&G', temperature: 'Temp', tds: 'TDS',
  conductivity: 'Cond.', lead: 'Pb', chromium: 'Cr',
  flowRate: 'Flow', turbidity: 'Turbidity', hardness: 'Hardness',
};
const PARAM_UNITS: Record<string, string> = {
  ph: '', bod: 'mg/L', cod: 'mg/L', tss: 'mg/L', nh3: 'mg/L',
  og: 'mg/L', oandg: 'mg/L', temperature: '°C', tds: 'mg/L',
  conductivity: 'µS/cm', lead: 'mg/L', chromium: 'mg/L',
  flowRate: 'm³/d', turbidity: 'NTU', hardness: 'mg/L',
};
function stdBadgeClass(std: string) {
  if (std === 'UK_EA')    return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (std === 'AU_EPA')   return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (std === 'EU_UWWTD') return 'bg-violet-500/10 text-violet-400 border-violet-500/20';
  if (std === 'US_EPA')   return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-slate-700 text-slate-400 border-slate-600';
}

/* ─── Component ──────────────────────────────────────────────────────────── */

export default function SamplesPage() {
  const navigate = useNavigate();
  const { currentCustomer, activeStandard, userProfile } = useSession();
  const { isAuthenticated, isAdmin, user } = useAuth();

  /* ── All hooks unconditionally first ── */
  const [influent, setInfluent]             = useState<SampleRow[]>([]);
  const [effluent, setEffluent]             = useState<SampleRow[]>([]);
  const [reports, setReports]               = useState<ReportRow[]>([]);
  const [sites, setSites]                   = useState<CustomerSite[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [loading, setLoading]               = useState(false);
  const [viewingReport, setViewingReport]   = useState<ReportRow | null>(null);
  const [generating, setGenerating]         = useState(false);
  /** sampleId currently being persisted (shows spinner on that row) */
  const [assigningId, setAssigningId]       = useState<number | null>(null);
  /** Admin auto-assign panel open */
  const [showAutoAssign, setShowAutoAssign] = useState(false);
  /**
   * Admin auto-assign draft: prefix → siteId the admin has selected.
   * Initialised from heuristic suggestions; admin can change before applying.
   */
  const [autoAssignDraft, setAutoAssignDraft] = useState<Record<string, string>>({});
  /** Expanded sample row id in the sample records section */
  const [expandedSampleId, setExpandedSampleId] = useState<number | null>(null);
  /** Hub tab: influent | discharge | reports */
  const [activeHubTab, setActiveHubTab] = useState<'influent' | 'discharge' | 'reports'>('influent');
  /** Hub-wide search */
  const [hubSearch, setHubSearch] = useState('');
  /** Expanded item id in hub sections (string to handle both numbers and report ids) */
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const dbCustId      = resolveDbCustId(currentCustomer);
  const currentUserId = user ? Number(user.id) : 0;
  const countryCode: string =
    (currentCustomer as any)?.country ||
    (userProfile as any)?.country ||
    'INT';

  /* ── Load samples & sites ── */
  const loadSamples = useCallback(async () => {
    if (!currentCustomer || !dbCustId) {
      setInfluent([]); setEffluent([]); setSites([]); return;
    }
    setLoading(true);
    try {
      // Load sites: merge DB (for authenticated users) with localStorage.
      // DB is the authoritative source after a fresh re-import; localStorage
      // is kept as fallback and for offline / local-only sessions.
      const localSites = loadSitesForCustomer(
        currentCustomer.id,
        (currentCustomer as any).serverId,
      );
      let custSites: CustomerSite[] = localSites;
      if (isAuthenticated && hasPhpBackend() && dbCustId > 0) {
        try {
          const apiSites = await apiListCustomerSites(dbCustId);
          if (Array.isArray(apiSites) && apiSites.length > 0) {
            // Normalise: ensure id is always a string so siteId comparisons are consistent
            const normSites: CustomerSite[] = apiSites.map((s: any) => ({
              ...s,
              id: String(s.id),
              customerId: String(s.customerId ?? s.customer_id ?? ''),
            }));
            // Merge: DB sites take precedence; add any local-only sites not yet in DB
            const dbIds = new Set(normSites.map(s => s.id));
            const localOnly = localSites.filter(s => !dbIds.has(s.id));
            custSites = [...normSites, ...localOnly];
          }
        } catch {
          // Network error — fall back to local sites
        }
      }
      setSites(custSites);

      const localAll = getAllLocalSamples()
        .filter(s => !currentUserId || !s.userId || s.userId === currentUserId)
        .filter(s => s.customerId != null ? Number(s.customerId) === dbCustId : false);

      const toRow = (s: any, type: 'influent' | 'effluent'): SampleRow => ({
        id: Number(s.id),
        customerId: s.customerId != null ? Number(s.customerId) : null,
        // Always stringify siteId — DB returns integers, localStorage has strings;
        // consistent String comparison prevents "No site" when data is DB-sourced.
        siteId: s.siteId != null ? String(s.siteId) : null,
        sampleName: s.sampleName || s.sample_name || 'Untitled',
        reportNumber: s.reportNumber || s.report_number || buildRegulatoryReportNumber(type === 'influent' ? 'INF' : 'EFF', s.standard || activeStandard || 'UK_EA'),
        params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
        standard: s.standard || activeStandard || 'UK_EA',
        sampleType: type,
        createdAt: s.createdAt || s.created_at || new Date().toISOString(),
      });

      if (isAuthenticated && hasPhpBackend()) {
        const opts = isAdmin ? { adminAll: true } : {};
        const [infRows, effRows] = await Promise.all([
          apiListInfluentSamples(dbCustId, opts).catch(() => []),
          apiListInfluentSamples(dbCustId, { ...opts, type: 'effluent' }).catch(() => []),
        ]);
        const serverInf = (infRows as any[])
          .filter(r => (r.sampleType ?? r.sample_type ?? 'influent') === 'influent')
          .map(r => toRow(r, 'influent'));
        const serverEff = (effRows as any[]).map(r => toRow(r, 'effluent'));
        const serverInfIds = new Set(serverInf.map(s => s.id));
        const serverEffIds = new Set(serverEff.map(s => s.id));
        const localInf = localAll
          .filter(s => !s.sampleType || s.sampleType === 'influent')
          .map(s => toRow(s, 'influent'))
          .filter(s => !serverInfIds.has(s.id));
        const localEff = localAll
          .filter(s => s.sampleType === 'effluent')
          .map(s => toRow(s, 'effluent'))
          .filter(s => !serverEffIds.has(s.id));
        setInfluent([...serverInf, ...localInf].sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setEffluent([...serverEff, ...localEff].sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      } else {
        const localInf = localAll
          .filter(s => !s.sampleType || s.sampleType === 'influent')
          .map(s => toRow(s, 'influent'));
        const localEff = localAll
          .filter(s => s.sampleType === 'effluent')
          .map(s => toRow(s, 'effluent'));
        setInfluent(localInf.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setEffluent(localEff.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      }
    } catch (err) {
      console.warn('SamplesPage: failed to load samples', err);
    } finally {
      setLoading(false);
    }
  }, [currentCustomer, dbCustId, currentUserId, isAuthenticated, isAdmin, activeStandard]);

  /* ── Load reports ── */
  const loadReports = useCallback(async () => {
    const serverRows: ReportRow[] = [];
    if (hasPhpBackend() && isAuthenticated) {
      try {
        const rows = await apiListReports();
        (rows || []).forEach((r: any) => {
          serverRows.push({
            id: `srv_${r.id}`,
            reportNumber: r.reportNumber || buildRegulatoryReportNumber('SMP', r.standard || (currentCustomer as any)?.standard || activeStandard || 'UK_EA'),
            reportTitle: r.reportTitle || 'Water Quality Assessment Report',
            customerName: r.customerName || undefined,
            siteName: r.siteName || undefined,
            siteAddress: r.siteAddress || undefined,
            sitePermitRef: r.sitePermitRef || undefined,
            siteJurisdiction: r.siteJurisdiction || undefined,
            samples: Array.isArray(r.samples) ? r.samples.map((s: any) => ({
              name: s.name || 'Unknown',
              date: s.date || new Date().toISOString().split('T')[0],
              params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
            })) : [],
            standard: r.standard || 'UK_EA',
            createdAt: r.createdAt || new Date().toISOString(),
            source: 'server',
          });
        });
      } catch (err: any) {
        console.warn('[SamplesPage] apiListReports failed:', err?.message);
      }
    }
    const localReports: ReportRow[] = (() => {
      try {
        const all: any[] = JSON.parse(localStorage.getItem('rb_sample_reports') || '[]');
        return all
          .filter(r => !currentUserId || !r.userId || Number(r.userId) === currentUserId)
          .filter(r => !dbCustId || !r.customerId || Number(r.customerId) === dbCustId)
          .map(r => ({
            id: `loc_${r.id}`,
            reportNumber: r.reportNumber || buildRegulatoryReportNumber('SMP', r.standard || (currentCustomer as any)?.standard || activeStandard || 'UK_EA'),
            reportTitle: r.reportTitle || 'Water Quality Assessment Report',
            customerName: r.customerName || undefined,
            siteName: r.siteName || undefined,
            siteAddress: r.siteAddress || undefined,
            sitePermitRef: r.sitePermitRef || undefined,
            siteJurisdiction: r.siteJurisdiction || undefined,
            samples: Array.isArray(r.samples) ? r.samples.map((s: any) => ({
              name: s.name || s.sampleName || 'Unknown',
              referenceId: s.referenceId || s.reportNumber || undefined,
              date: s.date || new Date().toISOString().split('T')[0],
              params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
            })) : [],
            standard: r.standard || 'UK_EA',
            createdAt: r.createdAt || new Date().toISOString(),
            source: 'local' as const,
          }));
      } catch { return []; }
    })();
    // Deduplicate: if a local report's number already exists on the server, omit the local copy
    const serverNumbers = new Set(serverRows.map(r => r.reportNumber));
    const uniqueLocal   = localReports.filter(r => !serverNumbers.has(r.reportNumber));
    setReports([...serverRows, ...uniqueLocal]);
  }, [isAuthenticated, currentUserId, dbCustId]);

  useEffect(() => {
    loadSamples();
    loadReports();
  }, [isAuthenticated, currentCustomer?.id]);

  /* ── ALL derived state — before any early returns ── */

  const std = getStandard((currentCustomer as any)?.standard || activeStandard || 'UK_EA');

  /** Per-site influent sample count */
  const siteCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    sites.forEach(s => { counts[s.id] = 0; });
    influent.forEach(s => {
      if (s.siteId && counts[s.siteId] !== undefined) counts[s.siteId]++;
    });
    return counts;
  }, [influent, sites]);

  /** Influent samples with no site assigned */
  const unlinkedInfluent = useMemo(
    () => influent.filter(s => !s.siteId),
    [influent],
  );

  /** Effluent samples with no site assigned */
  const unlinkedEffluent = useMemo(
    () => effluent.filter(s => !s.siteId),
    [effluent],
  );

  /** Influent samples strictly for the selected site */
  const siteInfluent = useMemo(() => {
    if (!selectedSiteId) return [];
    return influent.filter(s => s.siteId === selectedSiteId);
  }, [influent, selectedSiteId]);

  /** Effluent samples strictly for the selected site */
  const siteEffluent = useMemo(() => {
    if (!selectedSiteId) return [];
    return effluent.filter(s => s.siteId === selectedSiteId);
  }, [effluent, selectedSiteId]);

  const selectedSite = useMemo(
    () => sites.find(s => s.id === selectedSiteId) || null,
    [sites, selectedSiteId],
  );

  /**
   * A report can only be generated when:
   *  - a specific site is chosen
   *  - that site has ≥ 3 influent samples with siteId === selectedSiteId
   * Regulatory requirement: site-scoped, never cross-site.
   */
  const canGenerateReport = !!selectedSiteId && siteInfluent.length >= 3;
  const hasSites = sites.length > 0;
  const hasUnlinked = unlinkedInfluent.length > 0 || unlinkedEffluent.length > 0;

  /* ── Retroactive site assignment (with auto-cascade) ── */
  const handleAssignSite = useCallback((sampleId: number, siteId: string) => {
    setAssigningId(sampleId);
    try {
      const siteIdOrNull = siteId || null;

      // Detect the prefix of the sample being assigned
      const allSamples = [...influent, ...effluent];
      const target = allSamples.find(s => s.id === sampleId);
      const prefix = target
        ? extractSamplePrefix(target.sampleName, target.reportNumber)
        : '';

      // Find other unlinked samples with the same prefix (cascade targets)
      const cascadeIds = new Set<number>();
      if (prefix) {
        [...unlinkedInfluent, ...unlinkedEffluent].forEach(s => {
          if (s.id !== sampleId &&
              extractSamplePrefix(s.sampleName, s.reportNumber) === prefix) {
            cascadeIds.add(s.id);
          }
        });
      }

      // Persist the primary assignment + all cascade targets in one pass
      const allAssignments = [
        { sampleId, siteId: siteIdOrNull },
        ...Array.from(cascadeIds).map(id => ({ sampleId: id, siteId: siteIdOrNull })),
      ];
      persistBulkAssignments(allAssignments);

      // Optimistically update React state
      const patch = (prev: SampleRow[]) => prev.map(s =>
        s.id === sampleId || cascadeIds.has(s.id) ? { ...s, siteId: siteIdOrNull } : s
      );
      setInfluent(patch);
      setEffluent(patch);

      const site = sites.find(s => s.id === siteId);
      if (cascadeIds.size > 0) {
        toast.success(
          `Linked to "${site?.name}" · ${cascadeIds.size} matching sample${cascadeIds.size !== 1 ? 's' : ''} auto-assigned by prefix "${prefix}"`,
          { duration: 5000 },
        );
      } else {
        toast.success(site ? `Sample linked to "${site.name}"` : 'Site link cleared');
      }
    } catch {
      toast.error('Failed to save site assignment');
    } finally {
      setAssigningId(null);
    }
  }, [sites, influent, effluent, unlinkedInfluent, unlinkedEffluent]);

  /* ── Admin: build auto-assign prefix groups ── */
  const prefixGroups = useMemo(() => {
    const groups: Record<string, SampleRow[]> = {};
    [...unlinkedInfluent, ...unlinkedEffluent].forEach(s => {
      const p = extractSamplePrefix(s.sampleName, s.reportNumber);
      if (!groups[p]) groups[p] = [];
      groups[p].push(s);
    });
    return groups;
  }, [unlinkedInfluent, unlinkedEffluent]);

  /** Initialise the draft with heuristic suggestions whenever the panel opens */
  const handleOpenAutoAssign = useCallback(() => {
    const draft: Record<string, string> = {};
    Object.keys(prefixGroups).forEach(prefix => {
      const best = findBestSiteForPrefix(prefix, sites);
      draft[prefix] = best?.id || '';
    });
    setAutoAssignDraft(draft);
    setShowAutoAssign(true);
  }, [prefixGroups, sites]);

  /** Apply all admin draft assignments in one transaction */
  const handleApplyAutoAssign = useCallback(() => {
    const assignments: Array<{ sampleId: number; siteId: string | null }> = [];
    let totalSamples = 0;
    let sitesUsed = new Set<string>();

    Object.entries(autoAssignDraft).forEach(([prefix, siteId]) => {
      if (!siteId) return; // admin left this group unassigned
      const samples = prefixGroups[prefix] || [];
      samples.forEach(s => {
        assignments.push({ sampleId: s.id, siteId });
        totalSamples++;
      });
      sitesUsed.add(siteId);
    });

    if (assignments.length === 0) {
      toast.error('No assignments to apply — select a site for at least one group');
      return;
    }

    persistBulkAssignments(assignments);

    // Optimistically update React state
    const assignMap = new Map(assignments.map(a => [a.sampleId, a.siteId]));
    const patch = (prev: SampleRow[]) => prev.map(s => {
      const newSiteId = assignMap.get(s.id);
      return newSiteId !== undefined ? { ...s, siteId: newSiteId } : s;
    });
    setInfluent(patch);
    setEffluent(patch);
    setShowAutoAssign(false);

    toast.success(
      `Auto-assigned ${totalSamples} sample${totalSamples !== 1 ? 's' : ''} across ${sitesUsed.size} site${sitesUsed.size !== 1 ? 's' : ''}`,
      { duration: 5000 },
    );
  }, [autoAssignDraft, prefixGroups]);

  /* ── Generate site-scoped report ── */
  const handleGenerateReport = async () => {
    if (!selectedSiteId || !selectedSite) {
      toast.error('Select a site to generate a report');
      return;
    }
    if (siteInfluent.length < 3) {
      toast.error(`At least 3 influent samples from "${selectedSite.name}" are required`);
      return;
    }
    setGenerating(true);
    try {
      const reportNumber  = buildRegulatoryReportNumber('SMP', (currentCustomer as any)?.standard || activeStandard || 'UK_EA');
      const customerName  = currentCustomer?.company || currentCustomer?.name || 'Unknown';
      const siteAddress   = [selectedSite.address, selectedSite.postcode, selectedSite.country]
        .filter(Boolean).join(', ') || undefined;
      const sitePermitRef = selectedSite.permitRef || undefined;
      // Derive jurisdiction from site metadata (NOT user notes — regulatory accuracy)
      const siteJurisdiction = resolveSiteJurisdiction(selectedSite);

      /* Only samples recorded at THIS site — strictly scoped */
      const samplePayload = siteInfluent.slice(0, 12).map(s => ({
        name: s.sampleName,
        referenceId: s.reportNumber || undefined,
        date: s.createdAt.split('T')[0],
        params: s.params,
      }));

      const reportTitle = `${customerName} — ${selectedSite.name} Water Quality Report`;

      const reportData = {
        id: Date.now(),
        userId: currentUserId,
        customerId: dbCustId,
        siteId: selectedSite.id,
        reportNumber,
        reportTitle,
        customerName,
        siteName: selectedSite.name,
        siteAddress,
        sitePermitRef,
        siteJurisdiction,
        samples: samplePayload,
        standard: std.code,
        createdAt: new Date().toISOString(),
      };

      // Always save to localStorage first as an offline backup
      const existing: any[] = (() => {
        try { return JSON.parse(localStorage.getItem('rb_sample_reports') || '[]'); } catch { return []; }
      })();
      localStorage.setItem('rb_sample_reports', JSON.stringify([reportData, ...existing]));

      if (hasPhpBackend() && isAuthenticated) {
        try {
          await apiSaveReport({ ...reportData, customerId: dbCustId });
          // DB save succeeded — remove the localStorage copy to prevent duplicate entries
          const updated: any[] = (() => {
            try { return JSON.parse(localStorage.getItem('rb_sample_reports') || '[]'); } catch { return []; }
          })();
          localStorage.setItem(
            'rb_sample_reports',
            JSON.stringify(updated.filter((x: any) => x.id !== reportData.id)),
          );
          toast.success(`Report ${reportNumber} saved to database ✓`);
        } catch (dbErr: any) {
          // Keep in localStorage — DB is unavailable or errored
          toast.warning(
            `Report saved locally. DB sync failed: ${dbErr?.message || 'Connection error'}. Use "Sync to DB" in Reports page.`,
            { duration: 7000 },
          );
        }
      } else {
        toast.info(`Report ${reportNumber} saved locally (no DB connection).`, { duration: 5000 });
      }

      loadReports();
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     ALL hooks complete — early returns are now safe
     ═══════════════════════════════════════════════════════════════════════ */

  /* ── Customer gate ── */
  if (!currentCustomer) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Beaker} title="Sample Management Hub" subtitle="Manage influent and effluent water quality samples" />
        <div className="flex flex-col items-center justify-center py-24 rounded-2xl border border-amber-500/20 bg-amber-500/5">
          <Building2 className="w-14 h-14 text-amber-400/40 mb-5" />
          <p className="text-base font-bold text-amber-400 mb-2">No active customer session</p>
          <p className="text-sm text-slate-500 text-center max-w-md">
            Select a customer from the Customers page to view and manage sample records.
          </p>
          <button onClick={() => navigate('/customers')}
            className="mt-6 flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all">
            <Building2 className="w-4 h-4" /> Go to Customers
          </button>
        </div>
      </div>
    );
  }

  /* ── Report viewer ── */
  if (viewingReport) {
    return (
      <ProfessionalReport
        reportNumber={viewingReport.reportNumber}
        reportTitle={viewingReport.reportTitle}
        customerName={viewingReport.customerName}
        siteName={viewingReport.siteName}
        siteAddress={viewingReport.siteAddress}
        sitePermitRef={viewingReport.sitePermitRef}
        siteJurisdiction={viewingReport.siteJurisdiction}
        samples={viewingReport.samples}
        createdAt={viewingReport.createdAt}
        standard={viewingReport.standard}
        onBack={() => setViewingReport(null)}
      />
    );
  }

  /* ── Main UI ── */
  return (
    <div className="space-y-4">

      <PageHeader
        icon={Beaker}
        title="Sample Management Hub"
        subtitle="Manage influent and effluent water quality samples"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { loadSamples(); loadReports(); }}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 border border-transparent hover:border-slate-800/60 transition-all" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => navigate('/influent-entry')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
              <Plus className="w-3.5 h-3.5" /> Add Influent
            </button>
            <button onClick={() => navigate('/effluent-entry')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all">
              <Plus className="w-3.5 h-3.5" /> Add Discharge
            </button>
          </div>
        }
      />

      {/* ── Active Session Banner ── */}
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
        <Building2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
        <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">Active Customer:</span>
        <span className="text-sm font-bold text-white">{currentCustomer.company || currentCustomer.name}</span>
        {std && (
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 font-bold">
            {std.flag} {std.shortName}
          </span>
        )}
      </div>

      {/* ── 4 Metric Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rb-card p-4 border border-slate-800/50 hover:border-cyan-500/30 transition-all cursor-pointer"
          onClick={() => navigate('/influent')}>
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Droplets className="w-4 h-4 text-cyan-400" />
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
          </div>
          <p className="text-2xl font-black text-white mb-0.5">{loading ? '—' : influent.length}</p>
          <p className="text-xs font-semibold text-cyan-400">Influent Samples</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Raw water — entering treatment</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="rb-card p-4 border border-slate-800/50 hover:border-amber-500/30 transition-all cursor-pointer"
          onClick={() => navigate('/effluent')}>
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Beaker className="w-4 h-4 text-amber-400" />
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
          </div>
          <p className="text-2xl font-black text-white mb-0.5">{loading ? '—' : effluent.length}</p>
          <p className="text-xs font-semibold text-amber-400">Discharge Records</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Treated discharge quality</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rb-card p-4 border border-slate-800/50 hover:border-emerald-500/30 transition-all cursor-pointer"
          onClick={() => navigate('/reports')}>
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <FileText className="w-4 h-4 text-emerald-400" />
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
          </div>
          <p className="text-2xl font-black text-white mb-0.5">{reports.length}</p>
          <p className="text-xs font-semibold text-emerald-400">Reports</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Site-scoped regulatory reports</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rb-card p-4 border border-slate-800/50 hover:border-violet-500/30 transition-all">
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-violet-400" />
            </div>
          </div>
          <p className="text-2xl font-black text-white mb-0.5">{sites.length}</p>
          <p className="text-xs font-semibold text-violet-400">Sites</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Registered discharge sites</p>
        </motion.div>
      </div>

      {/* ── Global Search + Category Tabs ── */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            value={hubSearch}
            onChange={e => setHubSearch(e.target.value)}
            placeholder="Search samples, reports, reference IDs..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40"
          />
          {hubSearch && (
            <button onClick={() => setHubSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-800/50 rounded-xl p-1 w-fit flex-wrap">
          {([
            { key: 'influent', label: 'Influent', count: influent.length, color: 'cyan' },
            { key: 'discharge', label: 'Discharge Records', count: effluent.length, color: 'amber' },
            { key: 'reports', label: 'Reports', count: reports.length, color: 'emerald' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveHubTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeHubTab === tab.key
                  ? tab.key === 'influent'
                    ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20'
                    : tab.key === 'discharge'
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                    : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
            >
              {tab.label}
              <span className={`text-[9px] px-1 py-0.5 rounded font-black ${
                activeHubTab === tab.key
                  ? tab.key === 'influent' ? 'bg-cyan-500/20 text-cyan-400'
                  : tab.key === 'discharge' ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-slate-700 text-slate-500'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <AnimatePresence mode="wait">
        {activeHubTab === 'influent' && (
          <motion.div key="influent-tab" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
            {/* Influent Section */}
            <div className="space-y-2">
              {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-slate-800/50 rounded-xl animate-pulse" />)}</div>
              ) : (hubSearch ? influent.filter(s => s.sampleName.toLowerCase().includes(hubSearch.toLowerCase()) || s.reportNumber.toLowerCase().includes(hubSearch.toLowerCase())) : influent).length === 0 ? (
                <div className="rounded-xl border border-slate-800/50 p-8 text-center">
                  <Droplets className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No influent samples yet for this customer</p>
                  <button onClick={() => navigate('/influent-entry')}
                    className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all mx-auto">
                    <PenLine className="w-3.5 h-3.5" /> Add First Influent Sample
                  </button>
                </div>
              ) : (
                <>
                  {(hubSearch ? influent.filter(s => s.sampleName.toLowerCase().includes(hubSearch.toLowerCase()) || s.reportNumber.toLowerCase().includes(hubSearch.toLowerCase())) : influent).slice(0, 10).map(s => {
                    const itemKey = `inf-${s.id}`;
                    const isExp = expandedItemId === itemKey;
                    const site = sites.find(st => st.id === s.siteId);
                    const paramEntries = Object.entries(s.params || {});
                    const keyParams = paramEntries.filter(([k]) => ['ph','bod','bod5','tss','cod','nh3n','nh3'].includes(k.toLowerCase())).slice(0, 5);
                    const stdInfo = getStandard(s.standard as any);
                    return (
                      <div key={s.id} className={`rounded-xl border overflow-hidden transition-all duration-150 ${
                        isExp ? 'border-cyan-500/30 bg-rb-surface/60' : 'border-slate-800/40 bg-slate-800/20 hover:border-cyan-500/20 cursor-pointer'
                      }`}>
                        <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none" onClick={() => setExpandedItemId(isExp ? null : itemKey)}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isExp ? 'bg-cyan-500/15 border border-cyan-500/25' : 'bg-cyan-500/10 border border-cyan-500/15'}`}>
                            <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-mono text-cyan-400 font-bold">{s.reportNumber}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(s.standard)}`}>{s.standard}</span>
                              {site && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-800/50 flex items-center gap-0.5"><MapPin className="w-2 h-2" />{site.name}</span>}
                            </div>
                            <p className="text-xs font-semibold text-white truncate mt-0.5">{s.sampleName}</p>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{fmtDate(s.createdAt)}</span>
                              {keyParams.map(([k, v]) => (
                                <span key={k} className="text-[9px] px-1.5 py-0.5 rounded border bg-slate-800/50 text-slate-400 border-slate-800/50 font-mono">
                                  {(PARAM_LABELS[k] || k).toUpperCase()}: {typeof v === 'number' ? v.toFixed(1) : v}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                            <button onClick={() => navigate('/influent')} title="Open in Influent Records" className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all">
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform duration-150 ${isExp ? 'rotate-180 text-cyan-400' : ''}`} />
                        </div>
                        <AnimatePresence>
                          {isExp && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }} className="overflow-hidden">
                              <div className="border-t border-cyan-500/15 px-3 pt-2.5 pb-3 space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  {paramEntries.slice(0, 10).map(([k, v]) => (
                                    <div key={k} className="flex flex-col items-center bg-slate-800/40 border border-slate-800/40 rounded-lg px-2.5 py-1.5 min-w-[52px]">
                                      <span className="text-[9px] text-slate-500 font-semibold mb-0.5">{PARAM_LABELS[k] || k}</span>
                                      <span className="text-[11px] font-bold text-white font-mono">{typeof v === 'number' ? v.toFixed(1) : String(v)}</span>
                                      {PARAM_UNITS[k] && <span className="text-[9px] text-slate-600">{PARAM_UNITS[k]}</span>}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                  <button onClick={() => navigate('/influent')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
                                    <ChevronRight className="w-3 h-3" /> Open in Influent Records
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                  {influent.length > 10 && (
                    <button onClick={() => navigate('/influent')} className="w-full py-2 text-[10px] font-bold text-cyan-400 hover:text-cyan-300 transition-colors border border-cyan-500/15 rounded-xl bg-cyan-500/[0.02] hover:bg-cyan-500/5">
                      View all {influent.length} influent records in Influent Records →
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}

        {activeHubTab === 'discharge' && (
          <motion.div key="discharge-tab" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
            {/* Discharge Section */}
            <div className="space-y-2">
              {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-slate-800/50 rounded-xl animate-pulse" />)}</div>
              ) : (hubSearch ? effluent.filter(s => s.sampleName.toLowerCase().includes(hubSearch.toLowerCase()) || s.reportNumber.toLowerCase().includes(hubSearch.toLowerCase())) : effluent).length === 0 ? (
                <div className="rounded-xl border border-slate-800/50 p-8 text-center">
                  <Beaker className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No discharge records yet for this customer</p>
                  <button onClick={() => navigate('/effluent-entry')}
                    className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all mx-auto">
                    <PenLine className="w-3.5 h-3.5" /> Add First Discharge Sample
                  </button>
                </div>
              ) : (
                <>
                  {(hubSearch ? effluent.filter(s => s.sampleName.toLowerCase().includes(hubSearch.toLowerCase()) || s.reportNumber.toLowerCase().includes(hubSearch.toLowerCase())) : effluent).slice(0, 10).map(s => {
                    const itemKey = `eff-${s.id}`;
                    const isExp = expandedItemId === itemKey;
                    const site = sites.find(st => st.id === s.siteId);
                    const paramEntries = Object.entries(s.params || {});
                    return (
                      <div key={s.id} className={`rounded-xl border overflow-hidden transition-all duration-150 ${
                        isExp ? 'border-amber-500/30 bg-amber-500/[0.03]' : 'border-slate-800/40 bg-slate-800/20 hover:border-amber-500/20 cursor-pointer'
                      }`}>
                        <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none" onClick={() => setExpandedItemId(isExp ? null : itemKey)}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isExp ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                            <Beaker className="w-3.5 h-3.5 text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-mono text-amber-400 font-bold">{s.reportNumber}</span>
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/20 font-bold">DISCHARGE</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold ${stdBadgeClass(s.standard)}`}>{s.standard}</span>
                              {site && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/20 flex items-center gap-0.5 font-bold"><MapPin className="w-2 h-2" />{site.name}</span>}
                            </div>
                            <p className="text-xs font-semibold text-white truncate mt-0.5">{s.sampleName}</p>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{fmtDate(s.createdAt)}</span>
                              {paramEntries.slice(0, 3).map(([k, v]) => (
                                <span key={k} className="text-[9px] px-1.5 py-0.5 rounded border bg-slate-800/50 text-slate-400 border-slate-800/50 font-mono">
                                  {(PARAM_LABELS[k] || k).toUpperCase()}: {typeof v === 'number' ? v.toFixed(1) : v}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                            <button onClick={() => navigate('/effluent')} title="Open in Discharge Records" className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all">
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform duration-150 ${isExp ? 'rotate-180 text-amber-400' : ''}`} />
                        </div>
                        <AnimatePresence>
                          {isExp && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }} className="overflow-hidden">
                              <div className="border-t border-amber-500/20 px-3 pt-2.5 pb-3 space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  {paramEntries.slice(0, 10).map(([k, v]) => (
                                    <div key={k} className="flex flex-col items-center bg-slate-800/40 border border-slate-800/40 rounded-lg px-2.5 py-1.5 min-w-[52px]">
                                      <span className="text-[9px] text-slate-500 font-semibold mb-0.5">{PARAM_LABELS[k] || k}</span>
                                      <span className="text-[11px] font-bold text-white font-mono">{typeof v === 'number' ? v.toFixed(1) : String(v)}</span>
                                      {PARAM_UNITS[k] && <span className="text-[9px] text-slate-600">{PARAM_UNITS[k]}</span>}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                  <button onClick={() => navigate('/effluent')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all">
                                    <ChevronRight className="w-3 h-3" /> Open in Discharge Records
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                  {effluent.length > 10 && (
                    <button onClick={() => navigate('/effluent')} className="w-full py-2 text-[10px] font-bold text-amber-400 hover:text-amber-300 transition-colors border border-amber-500/15 rounded-xl bg-amber-500/[0.02] hover:bg-amber-500/5">
                      View all {effluent.length} discharge records →
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}

        {activeHubTab === 'reports' && (
          <motion.div key="reports-tab" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
            {/* Reports Section */}
            <div className="space-y-2">
              {(hubSearch ? reports.filter(r => r.reportTitle.toLowerCase().includes(hubSearch.toLowerCase()) || r.reportNumber.toLowerCase().includes(hubSearch.toLowerCase()) || (r.siteName || '').toLowerCase().includes(hubSearch.toLowerCase())) : reports).length === 0 ? (
                <div className="rounded-xl border border-slate-800/50 p-8 text-center">
                  <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No reports yet</p>
                  <p className="text-xs text-slate-600 mt-1">Generate a report from the panel below — requires 3+ linked influent samples</p>
                </div>
              ) : (
                <>
                  {(hubSearch ? reports.filter(r => r.reportTitle.toLowerCase().includes(hubSearch.toLowerCase()) || r.reportNumber.toLowerCase().includes(hubSearch.toLowerCase()) || (r.siteName || '').toLowerCase().includes(hubSearch.toLowerCase())) : reports).slice(0, 10).map(r => {
                    const itemKey = `rep-${r.id}`;
                    const isExp = expandedItemId === itemKey;
                    return (
                      <div key={r.id} className={`rounded-xl border overflow-hidden transition-all duration-150 ${
                        isExp ? 'border-emerald-500/30 bg-emerald-500/[0.02]' : 'border-slate-800/40 bg-slate-800/20 hover:border-emerald-500/20 cursor-pointer'
                      }`}>
                        <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none" onClick={() => setExpandedItemId(isExp ? null : itemKey)}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isExp ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
                            <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-mono text-rb-brand font-bold">{r.reportNumber}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(r.standard)}`}>{r.standard}</span>
                              {r.source === 'local' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold">LOCAL</span>}
                            </div>
                            <p className="text-xs font-semibold text-white truncate mt-0.5">{r.reportTitle}</p>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{fmtDate(r.createdAt)}</span>
                              <span className="text-[10px] text-slate-600">{r.samples.length} sample{r.samples.length !== 1 ? 's' : ''}</span>
                              {r.siteName && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{r.siteName}</span>}
                              {r.customerName && <span className="text-[10px] text-slate-500">{r.customerName}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setViewingReport(r)} title="Preview Report" className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform duration-150 ${isExp ? 'rotate-180 text-emerald-400' : ''}`} />
                        </div>
                        <AnimatePresence>
                          {isExp && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }} className="overflow-hidden">
                              <div className="border-t border-emerald-500/15 px-3 pt-2.5 pb-3 space-y-2">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {[
                                    { label: 'Report #', value: r.reportNumber, mono: true },
                                    { label: 'Standard', value: r.standard },
                                    { label: 'Samples', value: String(r.samples.length) },
                                    { label: 'Site', value: r.siteName || '—' },
                                  ].map(({ label, value, mono }) => (
                                    <div key={label} className="rounded-lg bg-slate-800/30 border border-slate-800/40 px-2.5 py-2">
                                      <p className="text-[9px] text-slate-600 uppercase tracking-wider font-bold mb-0.5">{label}</p>
                                      <p className={`text-xs font-bold text-white truncate ${mono ? 'font-mono text-rb-brand' : ''}`}>{value}</p>
                                    </div>
                                  ))}
                                </div>
                                {r.siteAddress && <p className="text-[10px] text-slate-500 px-1">{r.siteAddress}</p>}
                                <div className="flex items-center gap-2 pt-1">
                                  <button onClick={() => setViewingReport(r)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all">
                                    <TrendingUp className="w-3 h-3" /> View Full Report
                                  </button>
                                  <button onClick={() => navigate('/reports')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all">
                                    View all in Reports →
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                  {reports.length > 10 && (
                    <button onClick={() => navigate('/reports')} className="w-full py-2 text-[10px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors border border-emerald-500/15 rounded-xl bg-emerald-500/[0.02] hover:bg-emerald-500/5">
                      View all {reports.length} reports in Reports →
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════
          UNLINKED SAMPLES WARNING — samples with no site assigned
          ═══════════════════════════════════════════════════════════════════ */}
      {hasSites && hasUnlinked && (
        <div className="rb-card border border-amber-500/25 bg-amber-500/5 p-4 space-y-3">

          {/* Panel header */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
              <Link2 className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-amber-400">
                {unlinkedInfluent.length + unlinkedEffluent.length} sample{(unlinkedInfluent.length + unlinkedEffluent.length) !== 1 ? 's' : ''} not linked to a site
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Regulatory reports require all samples to be linked to a discharge site.
                Assign each sample to its site below — saved immediately.
                Assigning one sample auto-assigns all matching samples by prefix.
              </p>
            </div>
            {/* Admin auto-assign button */}
            {isAdmin && (
              <button
                onClick={showAutoAssign ? () => setShowAutoAssign(false) : handleOpenAutoAssign}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all shrink-0 border-violet-500/30 bg-violet-500/10 text-violet-400 hover:bg-violet-500/20"
              >
                <Wand2 className="w-3 h-3" />
                Auto-Assign
                {showAutoAssign
                  ? <ChevronUp className="w-3 h-3 ml-0.5" />
                  : <ChevronDown className="w-3 h-3 ml-0.5" />}
              </button>
            )}
          </div>

          {/* ── Admin Auto-Assign Panel ── */}
          {isAdmin && showAutoAssign && (
            <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-3.5 h-3.5 text-violet-400" />
                  <p className="text-xs font-bold text-violet-400">Auto-Assign by Prefix</p>
                </div>
                <p className="text-[10px] text-slate-500">
                  Heuristic site suggestions — review and adjust before applying
                </p>
              </div>

              {/* Prefix group rows */}
              <div className="space-y-2">
                {Object.entries(prefixGroups).map(([prefix, samples]) => {
                  const suggestedSite = findBestSiteForPrefix(prefix, sites);
                  const draftSiteId   = autoAssignDraft[prefix] || '';
                  const draftSite     = sites.find(s => s.id === draftSiteId);
                  const isConfident   = !!suggestedSite && draftSiteId === suggestedSite.id;

                  return (
                    <div key={prefix}
                      className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-2 items-center px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-800/40">

                      {/* Prefix badge + sample count */}
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded font-mono text-[11px] font-black bg-slate-700 text-white">
                          {prefix}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {samples.length} sample{samples.length !== 1 ? 's' : ''}
                        </span>
                        {isConfident && (
                          <CheckCircle2 className="w-3 h-3 text-emerald-400" title="Confident match" />
                        )}
                      </div>

                      {/* Sample names preview */}
                      <p className="text-[10px] text-slate-500 truncate hidden sm:block">
                        {samples.map(s => s.sampleName).join(' · ')}
                      </p>

                      {/* Site selector */}
                      <select
                        value={draftSiteId}
                        onChange={e => setAutoAssignDraft(prev => ({ ...prev, [prefix]: e.target.value }))}
                        className={`text-[11px] bg-slate-900 border rounded-lg px-2 py-1.5 focus:outline-none cursor-pointer shrink-0 min-w-[180px] ${
                          draftSiteId
                            ? 'border-violet-500/40 text-violet-300 focus:border-violet-500/70'
                            : 'border-slate-600 text-slate-400'
                        }`}
                      >
                        <option value="">— Skip this group —</option>
                        {sites.map(site => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                            {site.id === suggestedSite?.id ? ' ✓' : ''}
                          </option>
                        ))}
                      </select>

                      {/* Current selection label */}
                      {draftSite && (
                        <p className="text-[10px] text-violet-400 sm:col-start-2 sm:col-end-4">
                          → {draftSite.name}
                          {draftSite.address ? ` · ${draftSite.address}` : ''}
                          {draftSite.permitRef ? ` · Permit ${draftSite.permitRef}` : ''}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Apply / Cancel */}
              <div className="flex items-center justify-between pt-1">
                <p className="text-[10px] text-slate-600">
                  Groups left blank will not be assigned. You can still assign them manually below.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAutoAssign(false)}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyAutoAssign}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 transition-all"
                  >
                    <Wand2 className="w-3 h-3" /> Apply Auto-Assign
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Manual per-sample assignment (all users) ── */}

          {/* Unlinked influent */}
          {unlinkedInfluent.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Droplets className="w-3 h-3 text-cyan-400" /> Influent — assign site
                <span className="text-slate-400 font-normal ml-2 normal-case tracking-normal">
                  (selecting one auto-assigns matching prefix)
                </span>
              </p>
              {unlinkedInfluent.map(s => {
                const prefix = extractSamplePrefix(s.sampleName, s.reportNumber);
                return (
                  <div key={s.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-800/50">
                    <FlaskConical className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{s.sampleName}</p>
                      <p className="text-[10px] text-slate-600">
                        {fmtDate(s.createdAt)}
                        {prefix && <span className="ml-2 font-mono text-slate-500">prefix: {prefix}</span>}
                      </p>
                    </div>
                    <select
                      disabled={assigningId === s.id}
                      defaultValue=""
                      onChange={e => { if (e.target.value) handleAssignSite(s.id, e.target.value); }}
                      className="text-[11px] bg-slate-900 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-300 focus:outline-none focus:border-amber-500/60 cursor-pointer shrink-0"
                    >
                      <option value="" disabled>— Assign site —</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {/* Unlinked effluent */}
          {unlinkedEffluent.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Beaker className="w-3 h-3 text-rb-brand" /> Effluent — assign site
              </p>
              {unlinkedEffluent.map(s => {
                const prefix = extractSamplePrefix(s.sampleName, s.reportNumber);
                return (
                  <div key={s.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-800/50">
                    <Beaker className="w-3.5 h-3.5 text-rb-brand shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{s.sampleName}</p>
                      <p className="text-[10px] text-slate-600">
                        {fmtDate(s.createdAt)}
                        {prefix && <span className="ml-2 font-mono text-slate-500">prefix: {prefix}</span>}
                      </p>
                    </div>
                    <select
                      disabled={assigningId === s.id}
                      defaultValue=""
                      onChange={e => { if (e.target.value) handleAssignSite(s.id, e.target.value); }}
                      className="text-[11px] bg-slate-900 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-300 focus:outline-none focus:border-amber-500/60 cursor-pointer shrink-0"
                    >
                      <option value="" disabled>— Assign site —</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          REGULATORY REPORT GENERATION — site-mandatory
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="rb-card p-5 border border-slate-800/50 space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-bold text-white">Generate Regulatory Report</h3>
          <span className="ml-auto flex items-center gap-1 text-[10px] text-rb-brand px-2 py-0.5 rounded border border-rb-brand/20 bg-rb-brand/5 font-bold">
            <Lock className="w-3 h-3" /> Site-scoped only
          </span>
        </div>

        {!hasSites ? (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-400">No sites registered for this customer</p>
              <p className="text-[11px] text-slate-500 mt-1">
                Regulatory requirements mandate that water quality reports are linked to a specific discharge site.
                Add a site from the Customers page before generating reports.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Select Discharge Site <span className="text-red-400 ml-1">*</span>
              </label>
              <select
                value={selectedSiteId}
                onChange={e => setSelectedSiteId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">— Select a site —</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name}{site.address ? ` · ${site.address}` : ''} — {siteCounts[site.id] ?? 0} linked influent sample{(siteCounts[site.id] ?? 0) !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-600">
                Reports are generated from samples linked to the selected site only — cross-site data is never combined.
              </p>
            </div>

            {/* Selected site info strip */}
            {selectedSite && (
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-x-6 gap-y-1 px-3 py-2.5 rounded-lg bg-slate-800/50 border border-slate-800/40 text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-emerald-400" />
                    <strong className="text-white">{selectedSite.name}</strong>
                  </span>
                  {selectedSite.address && (
                    <span>{selectedSite.address}{selectedSite.postcode ? `, ${selectedSite.postcode}` : ''}</span>
                  )}
                  {selectedSite.permitRef && (
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-rb-brand" />
                      Permit: <strong className="text-rb-brand ml-1">{selectedSite.permitRef}</strong>
                    </span>
                  )}
                  <span className="w-full text-[10px] text-slate-500">
                    Jurisdiction: <span className="text-slate-400">{resolveSiteJurisdiction(selectedSite)}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Status row */}
            {selectedSiteId && (
              <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                canGenerateReport ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'
              }`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  canGenerateReport ? 'bg-emerald-500/15' : 'bg-amber-500/15'
                }`}>
                  {canGenerateReport
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${canGenerateReport ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {canGenerateReport
                      ? `${siteInfluent.length} influent sample${siteInfluent.length !== 1 ? 's' : ''} linked to ${selectedSite?.name} — ready to generate`
                      : `${siteInfluent.length} of 3 required influent samples linked to this site`}
                  </p>
                  {!canGenerateReport && (siteCounts[selectedSiteId] ?? 0) < 3 && unlinkedInfluent.length > 0 && (
                    <p className="text-[10px] text-amber-500/80 mt-0.5">
                      ↑ Assign {Math.max(0, 3 - (siteCounts[selectedSiteId] ?? 0))} more unlinked sample{Math.max(0, 3 - (siteCounts[selectedSiteId] ?? 0)) !== 1 ? 's' : ''} to this site above
                    </p>
                  )}
                  {canGenerateReport && selectedSite && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {selectedSite.permitRef && <>Permit: <span className="text-rb-brand font-mono">{selectedSite.permitRef}</span> · </>}
                      Standard: {std?.shortName}
                    </p>
                  )}
                </div>
                {canGenerateReport && (
                  <button onClick={handleGenerateReport} disabled={generating}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition-all disabled:opacity-50 shrink-0">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {generating ? 'Generating…' : 'Generate Report'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Regulatory Notice ── */}
      <div className="p-3 rounded-xl border border-slate-800/40 bg-slate-800/20 flex items-start gap-3">
        <Shield className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Regulatory Compliance</p>
          <p className="text-[10px] text-slate-600 leading-relaxed">
            Water quality reports are site-specific under {std?.name || 'applicable regulatory standards'}: each report covers
            sampling data from one designated discharge site or sampling point only — cross-site data is never combined.
            Site jurisdictions are determined from site state and country metadata, not user-entered notes, ensuring regulatory accuracy.
          </p>
        </div>
      </div>


    </div>
  );
}
